import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api.ts';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    Bot,
    MessageSquare,
    Settings,
    Shield,
    LogOut,
    ChevronRight,
    AlertTriangle,
    Sparkles
} from 'lucide-react';

interface AdminUser {
    username: string;
    role: string;
    avatar?: string;
}

const AdminPanel: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);

    useEffect(() => {
        const checkAdminAccess = async () => {
            try {
                const response = await fetch(`${API_URL}/admin/stats`, {
                    credentials: 'include'
                });

                if (response.status === 403 || response.status === 401) {
                    setAccessDenied(true);
                    setLoading(false);
                    return;
                }

                // Get user info
                const userResponse = await fetch(`${API_URL}/user`, {
                    credentials: 'include'
                });
                const userData = await userResponse.json();

                if (userData.success) {
                    setAdminUser({
                        username: userData.user.username,
                        role: 'admin',
                        avatar: userData.user.avatar
                    });
                }

                setLoading(false);
            } catch (error) {
                console.error('Admin access check failed:', error);
                setAccessDenied(true);
                setLoading(false);
            }
        };

        checkAdminAccess();
    }, []);

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <Shield size={48} style={{ marginBottom: '16px', color: '#4ecdc4' }} />
                    <p>Verifying admin access...</p>
                </div>
            </div>
        );
    }

    if (accessDenied) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
            }}>
                <div style={{
                    textAlign: 'center',
                    background: 'rgba(255, 107, 107, 0.1)',
                    padding: '40px',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 107, 107, 0.3)'
                }}>
                    <AlertTriangle size={64} style={{ marginBottom: '16px', color: '#ff6b6b' }} />
                    <h1 style={{ fontSize: '24px', marginBottom: '8px' }}>Access Denied</h1>
                    <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '24px' }}>
                        You don't have admin permissions.
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        style={{
                            background: 'linear-gradient(135deg, #4ecdc4, #44a08d)',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '12px',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: '600'
                        }}
                    >
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    const navItems = [
        { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
        { path: '/admin/users', icon: Users, label: 'Users' },
        { path: '/admin/characters', icon: Bot, label: 'Characters' },
        { path: '/admin/comments', icon: MessageSquare, label: 'Comments' },
        { path: '/admin/suggestions', icon: Sparkles, label: 'Suggestions' },
        { path: '/admin/system', icon: Settings, label: 'System' }
    ];

    const isActive = (path: string, exact?: boolean) => {
        if (exact) return location.pathname === path;
        return location.pathname.startsWith(path);
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            display: 'flex'
        }}>
            {/* Sidebar */}
            <aside style={{
                width: '260px',
                background: 'rgba(0,0,0,0.3)',
                backdropFilter: 'blur(20px)',
                borderRight: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                flexDirection: 'column',
                position: 'fixed',
                height: '100vh'
            }}>
                {/* Header */}
                <div style={{
                    padding: '24px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Shield size={24} color="white" />
                        </div>
                        <div>
                            <h1 style={{
                                color: 'white',
                                fontSize: '18px',
                                fontWeight: '700',
                                margin: 0
                            }}>
                                Admin Panel
                            </h1>
                            <p style={{
                                color: 'rgba(255,255,255,0.5)',
                                fontSize: '12px',
                                margin: 0
                            }}>
                                {adminUser?.username}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav style={{ flex: 1, padding: '16px' }}>
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path, item.exact);

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    color: active ? 'white' : 'rgba(255,255,255,0.6)',
                                    textDecoration: 'none',
                                    marginBottom: '4px',
                                    background: active
                                        ? 'linear-gradient(135deg, rgba(78, 205, 196, 0.2), rgba(68, 160, 141, 0.2))'
                                        : 'transparent',
                                    border: active
                                        ? '1px solid rgba(78, 205, 196, 0.3)'
                                        : '1px solid transparent',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <Icon size={20} />
                                <span style={{ flex: 1, fontWeight: active ? '600' : '400' }}>
                                    {item.label}
                                </span>
                                {active && <ChevronRight size={16} />}
                            </Link>
                        );
                    })}
                </nav>

                {/* Footer */}
                <div style={{
                    padding: '16px',
                    borderTop: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <button
                        onClick={() => navigate('/')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            borderRadius: '12px',
                            color: 'rgba(255,255,255,0.6)',
                            background: 'transparent',
                            border: '1px solid rgba(255,255,255,0.1)',
                            width: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <LogOut size={20} />
                        <span>Exit Panel</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main style={{
                flex: 1,
                marginLeft: '260px',
                padding: '32px',
                overflowY: 'auto'
            }}>
                <Outlet />
            </main>
        </div>
    );
};

export default AdminPanel;
